/**
 * Authentication state (docs/02, docs/08 §8.1).
 *
 * Holds the access token in memory only. The refresh token lives in a secure
 * HTTP-only cookie the browser manages (S-04), so a reload restores the
 * session by calling /auth/refresh rather than by reading a token from
 * storage where a script could find it.
 *
 * The cached user is kept in IndexedDB so the app can open offline and show
 * the right screens; it is never treated as proof of identity, because every
 * API call is authorised server-side on each request (S-06).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { auth, setAccessToken, setSessionLostHandler } from "@/lib/api";
import {
  clearLocalData,
  loadSession,
  requestPersistence,
  saveSession,
} from "@/lib/db";
import { ELEVATED_ROLES, EXPORT_ROLES, REFERENCE_ROLES, type Role, type User } from "@/lib/types";

interface AuthValue {
  user: User | null;
  /** True until the initial session restore has finished. */
  loading: boolean;
  /** Set when a session ends unexpectedly, so the login screen can say so. */
  sessionExpired: boolean;
  login: (email: string, password: string) => Promise<"ok" | "totp">;
  verifyTotp: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  /** True when the account has one of the given roles. */
  hasRole: (...roles: Role[]) => boolean;
  isElevated: boolean;
  canExport: boolean;
  canEditReference: boolean;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [partialToken, setPartialToken] = useState<string | null>(null);

  // Restore on load: try the refresh cookie, fall back to the cached user so
  // the app is usable offline (docs/08 §8.1).
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const cached = await loadSession();
      if (!cancelled && cached?.user) setUser(cached.user);

      const refreshed = await auth.refresh();
      if (cancelled) return;

      if (refreshed) {
        try {
          const fresh = await auth.me();
          if (cancelled) return;
          setUser(fresh);
          await saveSession(null, fresh);
        } catch {
          // Online but the session is gone: fall back to the cached identity
          // if there is one, so queued work is not stranded.
        }
      }
      if (!cancelled) setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setSessionLostHandler(() => {
      setSessionExpired(true);
      setUser(null);
      void saveSession(null, null);
    });
    return () => setSessionLostHandler(null);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setSessionExpired(false);
    const result = await auth.login(email, password);

    if ("totp_required" in result) {
      setPartialToken(result.partial_token);
      return "totp" as const;
    }

    setUser(result.user);
    await saveSession(null, result.user);
    await requestPersistence();
    return "ok" as const;
  }, []);

  const verifyTotp = useCallback(
    async (code: string) => {
      if (!partialToken) throw new Error("Start the sign-in again.");
      const result = await auth.verifyTotp(partialToken, code);
      setPartialToken(null);
      setUser(result.user);
      await saveSession(null, result.user);
      await requestPersistence();
    },
    [partialToken],
  );

  const logout = useCallback(async () => {
    try {
      await auth.logout();
    } finally {
      setAccessToken(null);
      setUser(null);
      // Refuses while work is queued, so signing out cannot lose entries.
      await clearLocalData();
    }
  }, []);

  const hasRole = useCallback(
    (...roles: Role[]) => (user ? roles.includes(user.role) : false),
    [user],
  );

  const value = useMemo<AuthValue>(
    () => ({
      user,
      loading,
      sessionExpired,
      login,
      verifyTotp,
      logout,
      hasRole,
      isElevated: user ? ELEVATED_ROLES.includes(user.role) : false,
      canExport: user ? EXPORT_ROLES.includes(user.role) : false,
      canEditReference: user ? REFERENCE_ROLES.includes(user.role) : false,
    }),
    [user, loading, sessionExpired, login, verifyTotp, logout, hasRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside an AuthProvider");
  return context;
}
